import React from "react";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { pressable } from "../../../app/a11y";
import { weightLabel } from "../overview/overviewModel";

import {
  fmt,
  rangeLabel,
  tagLabel,
  ROLE,
  ARC_ROLE,
  glyph,
  weightColors,
  CAST_COLOR,
  ticks,
  arcLanes,
  characterLanes,
  memoize,
  maxZoom,
  viewOf,
  isZoomed,
  viewPct,
  viewWidth,
  zoomView,
  revealView,
} from "./sceneModel";
import { trackDrag } from "./drag";

const TOP_ARCS = 6;
const TOP_CHARS = 5;
const STRIPE = "repeating-linear-gradient(135deg,#333 0 3px,#555 3px 6px)";

const zoomLabel = (z) => (z >= 10 || Math.abs(z - Math.round(z)) < 0.05 ? Math.round(z) : z.toFixed(1)) + "×";

// left of fraction f of the lanes' timeline, as .sc-band places it
const trackLeft = (f) => "calc(var(--sc-ml) - 1px + (100% - var(--sc-ml) + 1px) * " + f.toFixed(5) + ")";

// arc step marker, as drawn on the lanes (legend)
function RoleGlyph({ role }) {
  const g = glyph(role);
  return (
    <span
      style={{
        flex: "none",
        width: g.size + "px",
        height: g.size + "px",
        background: g.bg,
        border: g.border,
        borderRadius: g.radius,
        transform: "rotate(" + g.rotate + ")",
        boxSizing: "border-box",
      }}
    />
  );
}

// tooltip that follows the mouse; its own state so hovering doesn't re-render the map
class MapTip extends React.PureComponent {
  state = { tip: null };

  show(e, head, text) {
    this.setState({ tip: { x: e.clientX, y: e.clientY, head, text } });
  }

  hide() {
    if (this.state.tip) this.setState({ tip: null });
  }

  render() {
    const { tip } = this.state;
    if (!tip) return null;
    return (
      <div className="sc-tip" style={{ left: Math.max(8, Math.min(tip.x + 14, window.innerWidth - 328)) + "px", top: tip.y + 16 + "px" }}>
        <span>{tip.head}</span>
        {tip.text}
      </div>
    );
  }
}

// vertical line across the lanes at the mouse, with its time on the ruler
class MapCursor extends React.PureComponent {
  state = { f: null, label: "" };

  show(f, label) {
    this.setState({ f, label });
  }

  hide() {
    if (this.state.f !== null) this.setState({ f: null });
  }

  render() {
    const { f, label } = this.state;
    if (f === null) return null;
    return (
      <>
        <div className="sc-cursor" style={{ left: trackLeft(f) }} />
        <span className="sc-readout" style={{ left: trackLeft(f), transform: f < 0.03 ? "none" : f > 0.97 ? "translateX(-100%)" : "translateX(-50%)" }}>
          {label}
        </span>
      </>
    );
  }
}

// Story map: content tags, search, time ruler, scene ribbon and story-arc / character lanes.
// The lanes can be zoomed (buttons, ctrl/cmd + wheel) and panned (shift + wheel, pan bar).
export default class StoryMap extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { ribbonPx: 0, zoomPx: 0, z: 1, v0: 0 };
    this.lanesRef = React.createRef();
    this.ribbonRef = React.createRef();
    this.zoomRef = React.createRef();
    this.tipRef = React.createRef();
    this.cursorRef = React.createRef();
    this.getArcLanes = memoize(arcLanes);
    this.getCharLanes = memoize(characterLanes);
    this.getTicks = memoize(ticks);
    this.getView = memoize(viewOf);
    this.measure = this.measure.bind(this);
  }

  componentDidMount() {
    this.measure();
    if (window.ResizeObserver) {
      // the zoom controls change width with FIT and the language
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.ribbonRef.current);
      this.ro.observe(this.zoomRef.current);
    } else window.addEventListener("resize", this.measure);
    this.lanesRef.current.addEventListener("wheel", this.onWheel, { passive: false });
  }

  componentDidUpdate(prevProps) {
    if (!this.state.ribbonPx) this.measure();
    // keep the selected scene in the zoomed window
    const x = this.props.model.scenes[this.props.sel];
    if (x && prevProps.sel !== this.props.sel) this.updateView((v) => revealView(v, x));
  }

  componentWillUnmount() {
    if (this.ro) this.ro.disconnect();
    else window.removeEventListener("resize", this.measure);
    this.lanesRef.current.removeEventListener("wheel", this.onWheel);
    this.stopDrag && this.stopDrag();
  }

  measure() {
    const el = this.ribbonRef.current,
      zoom = this.zoomRef.current;
    if (el && el.clientWidth !== this.state.ribbonPx) this.setState({ ribbonPx: el.clientWidth });
    if (zoom && zoom.offsetWidth !== this.state.zoomPx) this.setState({ zoomPx: zoom.offsetWidth });
  }

  view() {
    return this.getView(this.props.model.duration, this.state.z, this.state.v0);
  }

  // fn(view) -> new view, or null to keep it; reads the latest state so rapid wheel events add up
  updateView(fn) {
    this.setState((st) => {
      const v = fn(viewOf(this.props.model.duration, st.z, st.v0));
      return v && (v.z !== st.z || v.v0 !== st.v0) ? { z: v.z, v0: v.v0 } : null;
    });
  }

  zoomBy = (k) => {
    const x = this.props.model.scenes[this.props.sel];
    this.updateView((v) => zoomView(v, v.z * k, undefined, undefined, x));
  };
  zoomIn = () => this.zoomBy(2);
  zoomOut = () => this.zoomBy(0.5);
  zoomFit = () => this.setState({ z: 1, v0: 0 });

  onWheel = (e) => {
    const el = this.ribbonRef.current,
      r = el && el.getBoundingClientRect();
    if (!r || r.width <= 0 || e.clientX < r.left) return;
    const fx = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      this.updateView((v) => zoomView(v, v.z * Math.exp(-e.deltaY * 0.004), v.v0 + fx * v.span, fx));
    } else if (isZoomed(this.view()) && (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY))) {
      e.preventDefault();
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      this.updateView((v) => viewOf(v.duration, v.z, v.v0 + (d / r.width) * v.span));
    }
  };

  // pan bar: the thumb follows the mouse; a press outside the thumb centers it there
  panDown = (e) => {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect(),
      v = this.view();
    const toT = (cx) => ((cx - r.left) / r.width) * v.duration;
    const t0 = toT(e.clientX),
      off = t0 >= v.v0 && t0 <= v.v0 + v.span ? t0 - v.v0 : v.span / 2;
    const pan = (ev) => this.updateView((w) => viewOf(w.duration, w.z, toT(ev.clientX) - off));
    pan(e);
    this.stopDrag = trackDrag(pan, "grabbing");
  };

  // cursor while the mouse is over the timeline (not the label column or the zoom controls)
  onLanesMove = (e) => {
    const el = this.ribbonRef.current,
      cursor = this.cursorRef.current,
      r = el && el.getBoundingClientRect();
    if (!cursor) return;
    const f = r && r.width > 0 ? (e.clientX - r.left) / r.width : -1;
    if (f < 0 || f > 1 || e.target.closest(".sc-zoom")) return cursor.hide();
    const v = this.view();
    cursor.show(f, fmt(v.v0 + f * v.span));
  };

  onLanesLeave = () => {
    this.cursorRef.current && this.cursorRef.current.hide();
    this.hideTip();
  };

  sceneTip = (e, x) => {
    const { t } = this.props;
    const head = t("Scene") + " " + (x.i + 1) + " · " + rangeLabel(x.s, x.e) + (x.isTransition ? " · " + t("transition") : "");
    this.tipRef.current && this.tipRef.current.show(e, head, x.title || t("Untitled scene"));
  };

  // arc step: its scene, role and what happens to the arc there
  stepTip = (e, m) => {
    const { t } = this.props;
    const head = t("Scene") + " " + (m.i + 1) + " · " + rangeLabel(m.s, m.e) + " · " + t(ARC_ROLE[m.role] || m.role);
    this.tipRef.current && this.tipRef.current.show(e, head, m.note);
  };

  hideTip = () => this.tipRef.current && this.tipRef.current.hide();

  render() {
    const { t, model, castTimes, sel, matches, matchCount, tag, query, lanes, arc, who, arcsAll, charsAll } = this.props;
    const { scenes } = model;
    const view = this.view();
    const zoomed = isZoomed(view);
    const cur = scenes[sel] || { s: 0, e: 0 };
    const arcLaneList = this.getArcLanes(model);
    const charLaneList = this.getCharLanes(model);
    const hasLanes = arcLaneList.length > 0 || charLaneList.length > 0;
    const mode = lanes === "arcs" && arcLaneList.length ? "arcs" : charLaneList.length ? "chars" : "arcs";
    const px = this.state.ribbonPx;

    // selection band, clipped to the window
    const bs = Math.max(cur.s, view.v0),
      be = Math.min(cur.e, view.v0 + view.span);
    const bandF = ((bs - view.v0) / view.span).toFixed(5),
      bandW = (Math.max(0, be - bs) / view.span).toFixed(5);
    // hide ticks whose (centered, ~34px) label would run under the zoom controls
    const tickLimit = px ? 1 - (this.state.zoomPx + 20) / px : 1;

    return (
      <div className="sc-map">
        <div className="sc-maphead">
          {model.tags.length > 0 && (
            <>
              <span className="sc-overline">{t("Content")}</span>
              <div style={{ flex: "none", display: "flex", gap: "6px" }}>
                {model.tags.map((g) => (
                  <span key={g} className="sc-seg" data-on={tag === g} {...pressable(() => this.props.onTag(g), { "aria-pressed": tag === g })}>
                    {t(tagLabel(g))}
                  </span>
                ))}
              </div>
            </>
          )}
          {matchCount !== null && matchCount !== undefined && (
            <span className="sc-matchcount" role="status">
              {t("{{count}} of {{total}} scenes", { count: matchCount, total: scenes.length })}
            </span>
          )}
          <div className="sc-search">
            <SearchIcon />
            <input
              value={query}
              aria-label={t("Search scenes, people, places")}
              onChange={this.props.onQuery}
              onKeyDown={(e) => e.key === "Escape" && this.props.onClearQuery()}
              placeholder={t("Search scenes, people, places")}
            />
            {query && (
              <IconButton className="sc-clear" size="small" title={t("Clear")} onClick={this.props.onClearQuery}>
                <CloseIcon />
              </IconButton>
            )}
          </div>
        </div>

        <div className="sc-lanes" ref={this.lanesRef} onMouseMove={this.onLanesMove} onMouseLeave={this.onLanesLeave}>
          <MapCursor ref={this.cursorRef} />
          {be > bs && (
            <div
              className="sc-band"
              style={{
                left: "calc(var(--sc-ml) - 1px + (100% - var(--sc-ml) + 1px) * " + bandF + ")",
                width: "calc((100% - var(--sc-ml) + 1px) * " + bandW + ")",
              }}
            />
          )}
          <div className="sc-lane" style={{ height: "22px" }}>
            <div />
            <div className="sc-track" style={{ height: "22px", zIndex: 3 }}>
              <div className="sc-ticks">
                {this.getTicks(view.span, view.v0)
                  .filter((k) => k.f <= tickLimit)
                  .map((k) => (
                    <span key={k.t} className="sc-tick" style={{ left: k.left, transform: k.f < 0.03 ? "none" : "translateX(-50%)" }}>
                      {k.label}
                    </span>
                  ))}
              </div>
              <div className="sc-zoom" ref={this.zoomRef}>
                {zoomed && (
                  <span className="sc-zbtn sc-zfit" title={t("Show whole episode")} {...pressable(this.zoomFit)}>
                    {t("Fit")}
                  </span>
                )}
                <span className="sc-zbtn" title={t("Zoom out")} aria-label={t("Zoom out")} data-off={!zoomed} {...pressable(this.zoomOut)}>
                  <RemoveIcon />
                </span>
                <span className="sc-zlabel" title={t("Ctrl/⌘ + scroll to zoom · Shift + scroll to pan")}>
                  {zoomLabel(view.z)}
                </span>
                <span className="sc-zbtn" title={t("Zoom in")} aria-label={t("Zoom in")} data-off={view.z >= maxZoom(view.duration) - 0.001} {...pressable(this.zoomIn)}>
                  <AddIcon />
                </span>
              </div>
            </div>
          </div>

          <div className="sc-lane" style={{ height: "26px", margin: "2px 0 4px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px", minWidth: 0 }}>
              {hasLanes ? (
                <div className="sc-segs">
                  {arcLaneList.length > 0 && (
                    <span className="sc-seg" data-on={mode === "arcs"} {...pressable(() => this.props.onLanes("arcs"), { "aria-pressed": mode === "arcs" })}>
                      {t("Story arcs")}
                    </span>
                  )}
                  {charLaneList.length > 0 && (
                    <span className="sc-seg" data-on={mode === "chars"} {...pressable(() => this.props.onLanes("chars"), { "aria-pressed": mode === "chars" })}>
                      {t("Characters")}
                    </span>
                  )}
                </div>
              ) : (
                <span />
              )}
              <span className="sc-overline" style={{ fontSize: "11px", lineHeight: 1 }}>
                {t("Scenes")}
              </span>
            </div>
            <div className="sc-track" ref={this.ribbonRef} style={{ height: "26px" }}>
              {scenes.map((x) => {
                const isSel = x.i === sel,
                  wv = ((x.e - x.s) / view.span) * 100;
                return (
                  <div
                    key={x.i}
                    className="sc-block"
                    onMouseMove={(e) => this.sceneTip(e, x)}
                    onMouseLeave={this.hideTip}
                    onClick={() => this.props.onSelect(x.i)}
                    style={{ left: viewPct(x.s, view), width: wv.toFixed(3) + "%", opacity: !matches || matches[x.i] || isSel ? 1 : 0.2, zIndex: isSel ? 2 : 1 }}
                  >
                    <div style={{ background: x.isTransition ? STRIPE : "#333", boxShadow: isSel ? "0 0 0 3px #006aff" : "none" }}>
                      {(wv * px) / 100 > 22 && <span>{x.i + 1}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {mode === "arcs" && arcLaneList.length > 0 && (
            <>
              {arcLaneList.slice(0, arcsAll ? arcLaneList.length : TOP_ARCS).map((a) => {
                const w = weightColors(a.weight),
                  line = a.scenes.has(sel) ? "var(--p-accent)" : "var(--p-accline)";
                return (
                  <div key={a.k} className="sc-lane sc-bordered" style={{ height: "24px" }}>
                    <div className="sc-lanelabel" data-on={arc === a.k} title={t("Show this arc")} {...pressable(() => this.props.onArc(a.k), { "aria-pressed": arc === a.k })}>
                      <span
                        className="sc-weight"
                        title={t(weightLabel(a.weight))}
                        style={{ width: "16px", height: "16px", background: w[0], color: w[1], fontSize: "10px", lineHeight: "16px" }}
                      >
                        {a.label}
                      </span>
                      <div className="sc-ellipsis" title={a.title} style={{ font: "600 13px/17px var(--font-ui)", letterSpacing: "-0.2px", color: "var(--p-ink)" }}>
                        {a.title}
                      </div>
                    </div>
                    <div className="sc-track" style={{ height: "24px" }}>
                      {a.marks.map((m, n) => (
                        <div
                          key={"s" + n}
                          className="sc-span"
                          onMouseMove={(e) => this.stepTip(e, m)}
                          onMouseLeave={this.hideTip}
                          onClick={() => this.props.onSelect(m.i)}
                          style={{ left: viewPct(m.s, view), width: viewWidth(m.s, m.e, view) }}
                        />
                      ))}
                      <div
                        style={{
                          position: "absolute",
                          top: "11px",
                          height: "2px",
                          left: viewPct(a.spanL, view),
                          width: viewWidth(a.spanL, a.spanR, view),
                          background: line,
                        }}
                      />
                      {a.continues && (
                        <div className="sc-tail" style={{ left: viewPct(a.spanR, view), width: viewWidth(a.spanR, model.duration, view), borderTopColor: line }} />
                      )}
                      {a.marks.map((m, n) => {
                        const g = glyph(m.role);
                        return (
                          <div
                            key={n}
                            className="sc-mark"
                            onMouseMove={(e) => this.stepTip(e, m)}
                            onMouseLeave={this.hideTip}
                            onClick={() => this.props.onSelect(m.i)}
                            style={{
                              left: viewPct(m.t, view),
                              width: g.size + "px",
                              height: g.size + "px",
                              background: g.bg,
                              border: g.border,
                              borderRadius: g.radius,
                              transform: "translate(-50%,-50%) rotate(" + g.rotate + ")",
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              <div className="sc-lane sc-bordered" style={{ alignItems: "start", paddingTop: "3px" }}>
                <div style={{ display: "flex" }}>
                  {arcLaneList.length > TOP_ARCS && (
                    <span className="sc-link" style={{ marginLeft: "-4px" }} {...pressable(this.props.onToggleArcs, { "aria-expanded": arcsAll })}>
                      {arcsAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      {arcsAll ? t("Show top 6") : t("Show all {{count}} story arcs", { count: arcLaneList.length })}
                    </span>
                  )}
                </div>
                <div className="sc-legend">
                  {Object.keys(ARC_ROLE).map((r) => (
                    <span key={r}>
                      <RoleGlyph role={r} />
                      {t(ARC_ROLE[r])}
                    </span>
                  ))}
                  <span>
                    <span className="sc-tail sc-tailkey" />
                    {t("Continues")}
                  </span>
                </div>
              </div>
            </>
          )}

          {mode === "chars" && charLaneList.length > 0 && (
            <>
              <div className="sc-lane sc-bordered" style={{ height: "18px" }}>
                <div className="sc-cast sc-casthead">
                  <span />
                  <span>{t("Scenes")}</span>
                  <span>{t("On screen")}</span>
                  <span>{t("Speaking")}</span>
                </div>
                <div />
              </div>
              {charLaneList.slice(0, charsAll ? charLaneList.length : TOP_CHARS).map((c) => {
                const ct = c.id === null ? null : castTimes[c.id];
                return (
                  <div key={c.name} className="sc-lane sc-bordered" style={{ height: "20px" }}>
                    <div className="sc-lanelabel sc-cast" data-on={who === c.name} title={t("Show this character")} {...pressable(() => this.props.onWho(c.name), { "aria-pressed": who === c.name })}>
                      <span className="sc-ellipsis" title={c.name} style={{ font: "600 12px/16px var(--font-ui)", color: "var(--p-ink)" }}>
                        {c.name}
                      </span>
                      <span>{c.cells.length}</span>
                      <span>{ct ? fmt(ct.screen) : "–"}</span>
                      <span>{ct ? fmt(ct.speech) : "–"}</span>
                    </div>
                    <div className="sc-track" style={{ height: "20px" }}>
                      {c.cells.map((cell) => {
                        const x = scenes[cell.i];
                        return (
                          <div
                            key={cell.i}
                            title={x.i + 1 + " · " + t(ROLE[cell.role] || cell.role) + " · " + (x.title || t("Untitled scene"))}
                            onClick={() => this.props.onSelect(x.i)}
                            style={{
                              position: "absolute",
                              top: "5px",
                              height: "10px",
                              left: viewPct(x.s, view),
                              width: viewWidth(x.s, x.e, view),
                              paddingRight: "1px",
                              boxSizing: "border-box",
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ height: "100%", borderRadius: "2px", background: CAST_COLOR[cell.role] || CAST_COLOR.background }} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {charLaneList.length > TOP_CHARS && (
                <div className="sc-bordered" style={{ display: "flex", paddingTop: "3px" }}>
                  <span className="sc-link" style={{ marginLeft: "-4px" }} {...pressable(this.props.onToggleChars, { "aria-expanded": charsAll })}>
                    {charsAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    {charsAll ? t("Show top 5") : t("Show all {{count}} characters", { count: charLaneList.length })}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {zoomed && (
          <div className="sc-lane" style={{ height: "10px" }}>
            <div />
            <div className="sc-panbar" title={t("Drag to pan")} onMouseDown={this.panDown}>
              <div style={{ left: ((view.v0 / view.duration) * 100).toFixed(3) + "%", width: ((view.span / view.duration) * 100).toFixed(3) + "%" }} />
            </div>
          </div>
        )}
        <MapTip ref={this.tipRef} />
      </div>
    );
  }
}
